import { Component } from 'react'
import * as React from 'react'
import { rem } from 'polished'
import type { IntlShape } from 'react-intl'
import { injectIntl } from 'react-intl'

import TextArea from 'components/Common/Form/Input/TextArea'
import TextButton from 'LuxKit/Button/TextButton'
import styled from 'styled-components'
import { formToObject } from 'lib/forms/formToObject'
import TextInput from 'components/Common/Form/Input/TextInput'
import Caption from 'LuxKit/Typography/Caption'
import { without } from 'lib/array/arrayUtils'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import CaptionTextBlock from 'LuxKit/TextBlocks/CaptionTextBlock'
import LineExclamationCircleIcon from 'LuxKit/Icons/line/LineExclamationCircleIcon'
import InputChip from 'LuxKit/Chips/InputChip'
import type { ShareConfig } from './ShareModal'

const Emails = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${rem(8)};
`

const SubmitButton = styled(TextButton)`
  align-self: flex-end;
`

interface Props {
  intl: IntlShape
  shareMessage: string
  onShare: (type: string) => void
  closeShareModal: () => void
  shareConfig: ShareConfig
  hasShareMessage?: boolean
}

interface State {
  friendsEmailList: Array<string>
  trySubmitMessage: string
  processing: boolean
  processed?: boolean
}

class ShareWithFriendForm extends Component<React.PropsWithChildren<Props>, State> {
  state: State = {
    friendsEmailList: [],
    trySubmitMessage: '',
    processing: false,
  }

  static defaultProps = {
    hasShareMessage: true,
  }

  onSubmitSuccess = () => {
    const { intl } = this.props
    this.props.onShare('email form')
    this.setState({
      trySubmitMessage: intl.formatMessage({
        id: 'nNaNPzzFAbzI-HOwPTJq',
        defaultMessage: 'Thanks for sharing!',
        description: 'Success notification displayed after sharing offer via email',
      }),
      processing: false,
      processed: true,
    })
    setTimeout(this.props.closeShareModal, 3000)
  }

  onSubmitFailure = (e: any, optMessage?: string) => {
    const { intl } = this.props
    let message = optMessage || intl.formatMessage({
      id: 'AyaZxD4Y2rj3AT3ECdKh',
      defaultMessage: 'Oops something went wrong, please try again',
      description: 'Generic error message displayed when sharing an offer via email fails unexpectedly',
    })
    if (e?.status === 429) {
      message = intl.formatMessage({
        id: 'subWs5O6pUGCi4Dp8dVO',
        defaultMessage: 'You have reached the limit for sending emails, please try again tomorrow.',
        description: 'Rate limit error message when user has sent too many share emails',
      })
    }
    this.setState({
      trySubmitMessage: message,
      processing: false,
      processed: true,
    })
  }

  handleEmailsResponse = (emailsReponse: Array<any>) => {
    const erroredEmails = emailsReponse.filter(el => el.error)

    if (erroredEmails.length === 0) {
      this.onSubmitSuccess()
    } else {
      const limitReachedEmails = erroredEmails.filter(el => el.error.status === 429)

      // if there is an error 429 (limit reached) - show limit reached message (even though some emails may have been sent before reaching the limit)
      if (limitReachedEmails.length > 0) {
        this.onSubmitFailure(limitReachedEmails[0])
      } else {
        const invalidEmails = erroredEmails.reduce((acc, cur) => {
          if (cur.error?.message?.includes('This email seems to be invalid')) {
            const invalidEmail = cur.error.message.split('This email seems to be invalid: ')[1]
            acc.push(invalidEmail)
          }
          return acc
        }, [])

        // limit is not reached, no invalid emails, so all other errors will be just passed on
        if (invalidEmails.length === 0) {
          this.onSubmitFailure(erroredEmails[0])
          return
        }

        const { intl } = this.props
        const invalidEmailsMessage = intl.formatMessage(
          {
            id: 'PDZBmlaXQtWHhxxpG2TD',
            defaultMessage: '{count, plural, one {This email seems to be invalid: {emails}} other {These emails seem to be invalid: {emails}}}',
            description: 'Error message listing invalid email addresses when sharing an offer',
          },
          { count: invalidEmails.length, emails: invalidEmails.join(', ') },
        )

        this.setState({ friendsEmailList: [] })
        this.onSubmitFailure(null, invalidEmailsMessage)
      }
    }
  }

  handleSubmit = async(event: React.FormEvent<HTMLFormElement>) => {
    const { shareConfig } = this.props
    event.preventDefault()
    this.setState({
      trySubmitMessage: '',
      processing: true,
      processed: false,
    })

    if (!this.state.friendsEmailList.length) {
      this.setState({
        trySubmitMessage: this.props.intl.formatMessage({
          id: 'VKKzBmgWYkvTLMzd_y03',
          defaultMessage: 'Email is not valid or empty',
          description: 'Validation error when no email addresses are provided in share form',
        }),
        processing: false,
      })
      return false
    }

    const formValues = formToObject<{ friendMessage: string }>(event.currentTarget)

    const promises = this.state.friendsEmailList.map(email => {
      const sendPromise = shareConfig.onSend?.({
        friend_email: email,
        message: formValues.friendMessage,
      })
      return sendPromise ? sendPromise.catch(err => ({ error: err })) : Promise.resolve({ error: new Error('onSend not configured') })
    })

    const emailsReponse = await Promise.all(promises)

    this.handleEmailsResponse(emailsReponse)
  }

  onEmailSubmit = (input: HTMLInputElement) => {
    if (input.validity.valid && input.value) {
      const email = input.value
      this.setState((prevState) => ({
        friendsEmailList: [...prevState.friendsEmailList, email],
      }))
      input.value = ''
    }
  }

  onEmailBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    this.onEmailSubmit(e.currentTarget)
  }

  onEmailKeydown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key.toLowerCase() === 'enter') {
      e.preventDefault()
      this.onEmailSubmit(e.currentTarget)
    }
  }

  onRemoveEmail = (e: React.MouseEvent<HTMLButtonElement>) => {
    const email = e.currentTarget.dataset.email

    if (email) {
      this.setState((prevState) => ({
        friendsEmailList: without(prevState.friendsEmailList, email),
      }))
    }
  }

  render() {
    const {
      intl,
      shareMessage,
    } = this.props

    const {
      processing,
      trySubmitMessage,
      friendsEmailList,
    } = this.state

    const emailLabel = intl.formatMessage({
      id: 'n11zJh5EvcUmhgoqKfqw',
      defaultMessage: 'Email addresses',
      description: 'Label for email addresses input field in share by email form',
    })
    const maxEmailsMessage = intl.formatMessage({
      id: 'UlfPz1mlTrGS5E-gDIbd',
      defaultMessage: 'You can only send a maximum of 20 emails per day.',
      description: 'Warning message when user has added 20 or more email recipients',
    })
    const personalMessageLabel = intl.formatMessage({
      id: '0q71_lFqyyqlBkD5dr-s',
      defaultMessage: 'Personal message:',
      description: 'Label for personal message text area in share by email form',
    })
    const messagePlaceholder = intl.formatMessage({
      id: 'dXGpM1hxybBl6OqQWzm0',
      defaultMessage: 'Message',
      description: 'Placeholder text for personal message text area in share by email form',
    })
    const sendingLabel = intl.formatMessage({
      id: 'EKXm--J7P6Q1Su_ZRTNU',
      defaultMessage: 'Sending',
      description: 'Button text while share email is being sent for sharing content',
    })
    const sendLabel = intl.formatMessage({
      id: '4BabkuCONxO7dPprCzbu',
      defaultMessage: 'Send',
      description: 'Button text to send share email for sharing content with others',
    })

    return (
      <form onSubmit={this.handleSubmit}>
        <VerticalSpacer gap={8}>
          <div>
            <TextInput
              type="email"
              label={emailLabel}
              onKeyDown={this.onEmailKeydown}
              onBlur={this.onEmailBlur}
              optionalIndicatorEnabled={false}
            />
            <Emails>
              {friendsEmailList.map(email => <InputChip
                key={email}
                data-email={email}
                onClick={this.onRemoveEmail}
                variant="filled"
              >
                {email}
              </InputChip>)}
            </Emails>
            {friendsEmailList.length >= 20 && <Caption variant="large" colour="critical">
              {maxEmailsMessage}
            </Caption>}
          </div>
          {!!shareMessage &&
            <TextArea
              label={personalMessageLabel}
              id="friendMessage"
              rows={4}
              required
              type="text"
              name="friendMessage"
              placeholder={messagePlaceholder}
              defaultValue={shareMessage}
            />}
          {trySubmitMessage && <CaptionTextBlock
            variant="large"
            weight="bold"
            startIcon={<LineExclamationCircleIcon colour="critical" />}
          >
            {trySubmitMessage}
          </CaptionTextBlock>}
          <SubmitButton
            kind="primary"
            fit="mobile-full-width"
            disabled={processing}
            type="submit"
          >
            {processing ? sendingLabel : sendLabel}
          </SubmitButton>
        </VerticalSpacer>
      </form>
    )
  }
}

export default injectIntl(ShareWithFriendForm)
